import { useDataProvider, useNotify, useRefresh, useTranslate } from "ra-core";
import { useRef, useState, type ReactNode } from "react";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useGestiona } from "../datos/useMiRol";
import { useAjustes } from "../datos/useAjustes";
import {
  claveEtapa,
  datoQuePideEtapa,
  ErrorEtapa,
  validarCambioEtapa,
} from "../dominio/etapas";
import { motivosPara } from "../dominio/analitica";
import type { Etapa, Negocio } from "../dominio/tipos";
import { CobroDialog } from "./CobroDialog";
import { MoverEtapaDialog } from "./MoverEtapaDialog";

/** 47 (Robert, 31/10/2026): a estas etapas no se pasa a mano sin decir por qué. */
const PIDEN_MOTIVO = ["no_interesado", "largo_plazo"] as const;
type EtapaConMotivo = (typeof PIDEN_MOTIVO)[number];
const pideMotivo = (e: Etapa): e is EtapaConMotivo =>
  (PIDEN_MOTIVO as readonly Etapa[]).includes(e);

type Pendiente = {
  proyecto: Negocio;
  destino: Etapa;
  orden?: number;
  alTerminar?: (movida: boolean) => void;
};

/**
 * Mover un proyecto a otra etapa a mano (arrastrar en el tablero, menú de la ficha), con
 * las reglas de validarCambioEtapa y lo que pida la etapa: fecha de consultoría, de
 * presentación o de vuelta, en Cobrado el registro del cobro y, desde la 47, el motivo al pasar
 * a «No interesado» o «Largo plazo». Devuelve `mover` y los diálogos, que hay que pintar.
 */
export const useMoverEtapa = (): {
  mover: (
    proyecto: Negocio,
    destino: Etapa,
    opciones?: { orden?: number; alTerminar?: (movida: boolean) => void },
  ) => void;
  dialogos: ReactNode;
} => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { ajustes } = useAjustes();
  const esAdmin = useEsAdmin();
  // 36: un consultor mueve Ventas (salvo Cobrado) en las fichas donde hace la consultoría.
  const gestiona = useGestiona();
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  // El diálogo del cobro avisa «registrado» y después se cierra: no es un «cancelar».
  const cobroRegistrado = useRef(false);

  const avisarError = (e: unknown) => {
    if (e instanceof ErrorEtapa) notify(e.clave, { type: "warning" });
    else
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
  };

  const ejecutar = async (
    p: Pendiente,
    extras: Record<string, unknown> = {},
    motivo?: string,
  ) => {
    try {
      if (motivo && pideMotivo(p.destino)) {
        await dataProvider.moverConMotivo(
          p.proyecto,
          p.destino,
          motivo,
          (extras.fecha_vuelta as string | undefined) ?? null,
        );
        if (p.orden !== undefined)
          await dataProvider.ordenarProyecto(p.proyecto.proyecto_id, p.orden);
      } else if (
        p.destino === "consultoria_agendada" &&
        ajustes.agenda.propia &&
        extras.fecha_reunion
      ) {
        // 41: agendar en el hueco elegido; la base elige quién la hace.
        await dataProvider.agendarConsultoria({
          negocio: p.proyecto,
          canal: "otro",
          inicio: String(extras.fecha_reunion),
        });
      } else {
        await dataProvider.moverProyecto(p.proyecto, p.destino, {
          ...extras,
          ...(p.orden !== undefined ? { orden: p.orden } : {}),
        });
      }
      notify("nevora.pipeline.movida", {
        type: "info",
        messageArgs: { etapa: translate(claveEtapa(p.destino)) },
      });
      p.alTerminar?.(true);
    } catch (e) {
      avisarError(e);
      p.alTerminar?.(false);
    } finally {
      refresh();
    }
  };

  const mover = (
    proyecto: Negocio,
    destino: Etapa,
    opciones: { orden?: number; alTerminar?: (movida: boolean) => void } = {},
  ) => {
    const p: Pendiente = { proyecto, destino, ...opciones };
    if (destino === proyecto.etapa) {
      p.alTerminar?.(false);
      return;
    }
    try {
      if (destino !== "cobrado")
        validarCambioEtapa(proyecto, destino, gestiona(proyecto));
      else if (!esAdmin)
        throw new ErrorEtapa("nevora.etapas_errores.solo_ejecucion");
    } catch (e) {
      avisarError(e);
      p.alTerminar?.(false);
      return;
    }
    if (
      destino === "cobrado" ||
      datoQuePideEtapa(destino) ||
      pideMotivo(destino)
    ) {
      setPendiente(p);
      return;
    }
    void ejecutar(p);
  };

  const cancelar = () => {
    pendiente?.alTerminar?.(false);
    setPendiente(null);
  };

  const dato = pendiente ? datoQuePideEtapa(pendiente.destino) : null;

  const dialogos = (
    <>
      {pendiente && dato === "cobro" ? (
        <CobroDialog
          proyecto={pendiente.proyecto}
          primero={pendiente.proyecto.embudo === "ventas"}
          open
          onOpenChange={(abierto) => {
            if (abierto) return;
            if (cobroRegistrado.current) {
              cobroRegistrado.current = false;
              setPendiente(null);
            } else {
              cancelar();
            }
          }}
          onRegistrado={() => {
            cobroRegistrado.current = true;
            pendiente.alTerminar?.(true);
          }}
        />
      ) : null}
      <MoverEtapaDialog
        nombre={pendiente?.proyecto.nombre ?? ""}
        destino={pendiente && dato !== "cobro" ? pendiente.destino : null}
        dato={dato && dato !== "cobro" ? dato : null}
        motivos={
          pendiente && pideMotivo(pendiente.destino)
            ? motivosPara(ajustes, pendiente.proyecto.etapa)
            : null
        }
        importeActual={
          pendiente?.proyecto.importe_total ??
          pendiente?.proyecto.valor_estimado
        }
        onCancelar={cancelar}
        onMover={(extras, motivo) => {
          const p = pendiente;
          setPendiente(null);
          if (p) void ejecutar(p, extras, motivo);
        }}
      />
    </>
  );

  return { mover, dialogos };
};
